import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Tractor, Wrench, AlertTriangle, Plus, ArrowRight, Clock,
  ListChecks, FolderKanban, Mail, FileText,
} from "lucide-react";
import api from "@/lib/api";
import { relativeDue, daysBetween, formatDate, currency } from "@/lib/utils2";
import { Button } from "@/components/ui/button";
import CostOfOwnershipWidget from "@/components/CostOfOwnershipWidget";
import AuthImage from "@/components/AuthImage";

const PLACEHOLDERS = {
  Work: "https://images.unsplash.com/photo-1589746164246-8f32d747c43c?crop=entropy&cs=srgb&fm=jpg&w=600&q=70",
  Home: "https://images.pexels.com/photos/7019609/pexels-photo-7019609.jpeg?auto=compress&cs=tinysrgb&w=600",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [recent, setRecent] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipmentMap, setEquipmentMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, eq, mt, pr] = await Promise.all([
          api.get("/dashboard"),
          api.get("/equipment"),
          api.get("/maintenance"),
          api.get("/projects"),
        ]);
        setData(d.data);
        setEquipment(eq.data.items);
        setRecent(mt.data.items.slice(0, 5));
        setProjects(pr.data.items);
        setEquipmentMap(Object.fromEntries(eq.data.items.map((x) => [x.equipment_id, x])));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="h-6 w-40 bg-zinc-900 animate-pulse rounded-sm mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-zinc-900 animate-pulse rounded-sm" />)}
        </div>
      </div>
    );
  }

  const stats = [
    { label: "Equipment", value: data?.equipment_count ?? 0, icon: Tractor, tone: "text-orange-500", testid: "stat-equipment" },
    { label: "Log Entries", value: data?.maintenance_count ?? 0, icon: Wrench, tone: "text-zinc-200", testid: "stat-maintenance" },
    { label: "Projects", value: data?.projects_count ?? 0, icon: FolderKanban, tone: "text-zinc-200", testid: "stat-projects" },
    { label: "Overdue", value: data?.overdue_count ?? 0, icon: AlertTriangle, tone: data?.overdue_count > 0 ? "text-red-500" : "text-zinc-200", testid: "stat-overdue" },
  ];

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1600px] mx-auto" data-testid="dashboard">
      {/* Feedback banner */}
      <a
        href="mailto:maintainhq@gmail.com?subject=MaintainHQ%20Feedback&body=Hi%20—%20%0A%0A"
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-zinc-900 border border-zinc-800 hover:border-orange-600 rounded-sm px-4 py-3 mb-6 transition-colors group"
        data-testid="feedback-banner"
      >
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-orange-500 shrink-0" />
          <div>
            <div className="text-sm font-medium">Got an idea or hit a snag?</div>
            <div className="text-xs text-zinc-500">We read every email — features, fixes, frustrations welcome.</div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 group-hover:translate-x-1 transition-transform">
          maintainhq@gmail.com →
        </div>
      </a>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// Shop floor</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Dashboard</h1>
          <p className="text-zinc-400 mt-2">Today's schedule and shop health.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/app/equipment/new">
            <Button className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm uppercase tracking-widest font-bold text-xs" data-testid="add-equipment-cta">
              <Plus className="w-4 h-4 mr-1" /> Add equipment
            </Button>
          </Link>
          <Link to="/app/schedule">
            <Button variant="secondary" className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-sm uppercase tracking-widest font-bold text-xs">
              View tasks
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 sm:p-5" data-testid={s.testid}>
            <div className="flex items-start justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.label}</div>
              <s.icon className={`h-4 w-4 ${s.tone}`} />
            </div>
            <div className={`font-display font-black text-4xl tracking-tighter ${s.tone}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Upcoming tasks */}
      <SectionHeader
        eyebrow="Next 30 days"
        title="Upcoming maintenance"
        link="/app/schedule"
        linkLabel="All tasks"
      />
      <section className="bg-zinc-900 border border-zinc-800 rounded-sm mb-8">
        {data?.upcoming?.length ? (
          <ul className="divide-y divide-zinc-800">
            {data.upcoming.map((u) => {
              const d = daysBetween(u.due_date);
              const overdue = d !== null && d < 0;
              return (
                <li key={u.schedule_id} className="px-5 py-4 flex items-center gap-4 hover:bg-zinc-800/40 transition-colors">
                  <div className={`w-1 h-12 ${overdue ? "bg-red-500" : d <= 7 ? "bg-orange-500" : "bg-zinc-700"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{u.task}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">
                      <Link to={`/app/equipment/${u.equipment_id}`} className="hover:text-orange-400">
                        {u.equipment_name || "Equipment"}
                      </Link>
                      {u.equipment_type ? <span className="mx-2 text-zinc-700">·</span> : null}
                      {u.equipment_type}
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end">
                    <div className={`text-sm font-mono ${overdue ? "text-red-400" : d <= 7 ? "text-orange-400" : "text-zinc-300"}`}>
                      {relativeDue(u.due_date)}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(u.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyHint
            icon={ListChecks}
            text="No upcoming tasks. Add equipment to autopopulate a schedule."
            cta="Add equipment"
            to="/app/equipment/new"
          />
        )}
      </section>

      {/* Garage preview */}
      <SectionHeader
        eyebrow="Asset registry"
        title="Garage"
        link="/app/equipment"
        linkLabel="View all"
      />
      <section className="mb-8">
        {equipment.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {equipment.slice(0, 4).map((eq) => (
              <Link
                key={eq.equipment_id}
                to={`/app/equipment/${eq.equipment_id}`}
                className="group bg-zinc-900 border border-zinc-800 hover:border-orange-600 rounded-sm overflow-hidden transition-all"
                data-testid={`dash-equipment-${eq.equipment_id}`}
              >
                <div className="aspect-[4/3] bg-zinc-950 relative">
                  {eq.photo_ids?.[0] ? (
                    <AuthImage fileId={eq.photo_ids[0]} className="w-full h-full object-cover" />
                  ) : (
                    <img src={PLACEHOLDERS[eq.category] || PLACEHOLDERS.Home} alt="" className="w-full h-full object-cover opacity-70" />
                  )}
                  <div className="absolute top-2 left-2 tag-pill bg-zinc-950/90 text-orange-500 border border-orange-600/50">
                    {eq.category}
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-display font-bold text-base tracking-tight truncate group-hover:text-orange-500 transition-colors">{eq.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{eq.equipment_type}</div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyHint
            icon={Tractor}
            text="Empty garage. Pick from 70+ pre-loaded types."
            cta="Add first equipment"
            to="/app/equipment/new"
          />
        )}
      </section>

      {/* Recent maintenance */}
      <SectionHeader
        eyebrow="Latest activity"
        title="Recent log entries"
        link="/app/equipment"
        linkLabel="View all"
      />
      <section className="bg-zinc-900 border border-zinc-800 rounded-sm mb-8">
        {recent.length ? (
          <ul className="divide-y divide-zinc-800">
            {recent.map((r) => {
              const eq = equipmentMap[r.equipment_id];
              return (
                <li key={r.record_id} className="px-5 py-4 flex items-center gap-3 hover:bg-zinc-800/40 transition-colors">
                  <Wrench className="w-4 h-4 text-orange-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.task}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">
                      {eq ? <Link to={`/app/equipment/${eq.equipment_id}`} className="hover:text-orange-400">{eq.name}</Link> : "Equipment"}
                      <span className="mx-2 text-zinc-700">·</span>
                      <span className="font-mono">{formatDate(r.performed_at)}</span>
                      {r.cost != null ? <><span className="mx-2 text-zinc-700">·</span><span className="text-orange-500 font-mono">{currency(r.cost)}</span></> : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyHint
            icon={Wrench}
            text="No maintenance logged yet. Open any equipment to log work."
          />
        )}
      </section>

      {/* Projects strip */}
      <SectionHeader
        eyebrow="Task bundles"
        title="Active projects"
        link="/app/projects"
        linkLabel="All projects"
      />
      <section className="mb-8">
        {projects.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.slice(0, 3).map((p) => (
              <Link
                key={p.project_id}
                to="/app/projects"
                className="group bg-zinc-900 border border-zinc-800 hover:border-orange-600 rounded-sm p-4 transition-colors"
                style={{ borderLeft: `3px solid ${p.color || "#ea580c"}` }}
              >
                <FolderKanban className="w-5 h-5 mb-2" style={{ color: p.color || "#ea580c" }} />
                <div className="font-display font-bold text-base tracking-tight group-hover:text-orange-500 transition-colors">{p.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">started {formatDate(p.created_at)}</div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyHint
            icon={FolderKanban}
            text="No projects yet. Try a template like 'Spring Planting' or 'Winterize'."
            cta="Browse templates"
            to="/app/projects"
          />
        )}
      </section>

      {/* Cost of ownership */}
      <SectionHeader eyebrow="Bottom line" title="Cost of ownership" inline />
      <CostOfOwnershipWidget />

      {/* Reports CTA */}
      <Link
        to="/app/reports"
        className="block mt-8 bg-zinc-900 border border-zinc-800 hover:border-orange-600 rounded-sm p-5 group transition-colors"
        data-testid="dash-reports-link"
      >
        <div className="flex items-center gap-4">
          <FileText className="w-6 h-6 text-orange-500" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg tracking-tight">Generate a report</div>
            <div className="text-xs text-zinc-500 mt-0.5">PDF service histories, tax-time exports, resale packets.</div>
          </div>
          <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
        </div>
      </Link>
    </div>
  );
}

function SectionHeader({ eyebrow, title, link, linkLabel, inline }) {
  return (
    <div className={`flex items-center justify-between ${inline ? "mb-3" : "mb-3"}`}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">// {eyebrow}</div>
        <h2 className="font-display font-bold text-xl tracking-tight">{title}</h2>
      </div>
      {link ? (
        <Link to={link} className="text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400 inline-flex items-center gap-1">
          {linkLabel} <ArrowRight className="h-3 w-3" />
        </Link>
      ) : null}
    </div>
  );
}

function EmptyHint({ icon: Icon, text, cta, to }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm px-6 py-8 text-center">
      <Icon className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
      <div className="text-zinc-400 text-sm mb-3">{text}</div>
      {cta && to ? (
        <Link to={to}>
          <Button size="sm" className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold">
            {cta}
          </Button>
        </Link>
      ) : null}
    </div>
  );
}
